// Packages
import { Component, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

/**
 * `fx-backdrop` is a full-screen overlay used by components such as side-overs
 * and toasts. It is headless: its default styles live in the global theme
 * (`core/src/themes/fx-backdrop.scss`) and target `fx-backdrop` and
 * `fx-backdrop::part(backdrop)`. Consumers can theme the overlay through those
 * selectors and CSS custom properties, while state is reflected via `data-*`
 * attributes on the host.
 *
 * @part backdrop - The full-screen backdrop overlay element.
 */
@Component({
  tag: "fx-backdrop",
  shadow: true,
})
export class FxBackdrop {
  // ----------------------------------------------
  // Props
  // ----------------------------------------------

  /**
   * @prop - If `true`, the `click` event will stop propagating after the
   * `fxBackdropTap` event is emitted.
   */
  @Prop() stopPropagation = true;

  /**
   * @prop - If `true`, the backdrop will emit `fxBackdropTap` when tapped.
   * When `false`, pointer events pass through to the underlying content.
   */
  @Prop() tappable = true;

  /**
   * @prop - If `true`, the backdrop overlay is rendered.
   */
  @Prop() visible = true;

  // ----------------------------------------------
  // Events
  // ----------------------------------------------

  /**
   * @event - Emitted when the backdrop is tapped and `tappable` is `true`.
   */
  @Event() fxBackdropTap!: EventEmitter<void>;

  // ----------------------------------------------
  // Handlers
  // ----------------------------------------------

  private onBackdropTap = (ev: MouseEvent) => {
    if (!this.visible || !this.tappable) {
      return;
    }

    if (this.stopPropagation) {
      ev.stopPropagation();
    }

    this.fxBackdropTap.emit();
  };

  // ----------------------------------------------
  // Render
  // ----------------------------------------------

  render() {
    const { visible, tappable } = this;

    return (
      <Host
        aria-hidden={visible ? null : "true"}
        data-tappable={tappable ? "true" : "false"}
        data-visible={visible ? "true" : "false"}
      >
        <div part="backdrop" onClick={this.onBackdropTap} />
      </Host>
    );
  }
}
